import type { SelectedSkillReference } from "./types";

export interface MessageSkillToken<T extends SelectedSkillReference = SelectedSkillReference> {
  start: number;
  end: number;
  skill: T;
}

export type UserMessageSkillSegment<T extends SelectedSkillReference = SelectedSkillReference> =
  | { type: "text"; text: string }
  | { type: "skill"; skill: T };

export interface UserMessageSkillContentModel<
  T extends SelectedSkillReference = SelectedSkillReference,
> {
  /** Skills selected for the turn whose `/slug` is not in the message body. */
  prepended: T[];
  /** Message body with matching `/slug` tokens replaced by Skill chips. */
  segments: UserMessageSkillSegment<T>[];
  hasInlineSkill: boolean;
}

/**
 * Resolves whitespace-bounded `/slug` tokens against skills already known for
 * the turn. Unlike the composer parser this does not consult `enabled` or cap
 * at three — persisted chat identity is authoritative.
 */
export function parseSkillTokensFromKnownSkills<T extends SelectedSkillReference>(
  value: string,
  skills: readonly T[]
): MessageSkillToken<T>[] {
  const skillsBySlug = new Map<string, T>();
  for (const skill of skills) {
    const key = skill.slug.toLowerCase();
    if (!skillsBySlug.has(key)) skillsBySlug.set(key, skill);
  }

  const resolved: MessageSkillToken<T>[] = [];
  const seenIds = new Set<string>();
  const seenSlugs = new Set<string>();
  const tokenPattern = /(^|\s)(\/[^\s/]+)(?=\s|$)/g;

  for (const match of value.matchAll(tokenPattern)) {
    const token = match[2];
    const skill = skillsBySlug.get(token.slice(1).toLowerCase());
    if (!skill || seenIds.has(skill.id) || seenSlugs.has(skill.slug)) continue;

    const start = (match.index ?? 0) + match[1].length;
    resolved.push({
      start,
      end: start + token.length,
      skill,
    });
    seenIds.add(skill.id);
    seenSlugs.add(skill.slug);
  }

  return resolved;
}

/**
 * Splits a sent user message so `/slug` tokens become Skill chips in place
 * (web conversation-thread behaviour). Skills that were selected without a
 * matching token — for example inferred Skills — are returned as `prepended`.
 */
export function buildUserMessageSkillContent<T extends SelectedSkillReference>(
  content: string,
  skills: readonly T[]
): UserMessageSkillContentModel<T> {
  if (skills.length === 0) {
    return {
      prepended: [],
      segments: content.length > 0 ? [{ type: "text", text: content }] : [],
      hasInlineSkill: false,
    };
  }

  const tokens = parseSkillTokensFromKnownSkills(content, skills);
  const matchedIds = new Set(tokens.map((token) => token.skill.id));
  const prepended = skills.filter((skill) => !matchedIds.has(skill.id));

  const segments: UserMessageSkillSegment<T>[] = [];
  let cursor = 0;
  for (const token of tokens) {
    if (token.start > cursor) {
      segments.push({ type: "text", text: content.slice(cursor, token.start) });
    }
    segments.push({ type: "skill", skill: token.skill });
    cursor = token.end;
  }
  if (cursor < content.length) {
    segments.push({ type: "text", text: content.slice(cursor) });
  }

  return { prepended, segments, hasInlineSkill: tokens.length > 0 };
}
